import { Fragment, useCallback } from "react";
import "./App.css";
import { Analytics } from "@vercel/analytics/react";
import { track } from "@vercel/analytics";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import SiteHeader from "./components/SiteHeader";
import SiteFooter from "./components/SiteFooter";
import BlogIndexPage from "./blog/BlogIndexPage";
import BlogPostPage from "./blog/BlogPostPage";
import NotFoundPage from "./pages/NotFoundPage";
import TermsPage from "./pages/TermsPage";
import InvestorsPage, { INVESTOR_MAILTO_HREF } from "./pages/InvestorsPage";
import { homepageCopy } from "./content/homepageCopy";
import { getStaticRouteMeta, useDocumentMeta } from "./seo/useDocumentMeta";
import {
  isModifiedClick,
  parseHashHref,
  scrollToHash,
  useScrollToHash,
} from "./seo/useScrollToHash";

const DEMO_MAILTO_HREF =
  "mailto:info@looporadata.ai?subject=" +
  encodeURIComponent("Demo request from LooporaData.ai homepage") +
  "&body=" +
  encodeURIComponent(
    [
      "Hi LooporaData team,",
      "",
      "I'd like to request a demo.",
      "",
      "My name:",
      "My organization:",
      "Best way to reach me:",
      "",
      "What I'm looking for:",
      "",
      "Thanks,",
      "",
    ].join("\n")
  );

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const pageName = getPageName(location.pathname);
  const isInvestors = pageName === "investors";
  useDocumentMeta(getStaticRouteMeta(location.pathname));
  useScrollToHash();

  const trackClick = (event, props = {}) => {
    track(event, { page: pageName, ...props });
  };

  const onHashLinkClick = useCallback(
    (event, href) => {
      if (!href || !event || event.defaultPrevented || isModifiedClick(event)) {
        return;
      }
      const target = parseHashHref(href);
      if (!target.hash) return;
      event.preventDefault();
      if (
        location.pathname === target.pathname &&
        location.hash === target.hash
      ) {
        scrollToHash(target.hash);
        return;
      }
      navigate({ pathname: target.pathname, hash: target.hash });
    },
    [location.hash, location.pathname, navigate]
  );

  return (
    <div className="app-shell">
      <a className="skip-link" href="#top">
        Skip to main content
      </a>
      <Analytics />
      <SiteHeader
        logoSrc={`${process.env.PUBLIC_URL || ""}/images/ld-logo.png`}
        logoAlt="LooporaData"
        homeHref="/"
        navItems={[
          { label: "Why LooporaData", href: "/#problem" },
          { label: "How It Works", href: "/#solution" },
          { label: "Blog", href: "/blog" },
        ]}
        ctaLabel={
          isInvestors ? "Request investor deck" : "Discover Your Data's Value"
        }
        ctaHref={isInvestors ? INVESTOR_MAILTO_HREF : "/#contact"}
        onCtaClick={(event) => {
          if (isInvestors) {
            trackClick("investor_cta_click", {
              location: "header",
              href: INVESTOR_MAILTO_HREF,
            });
            return;
          }
          trackClick("demo_cta_click", {
            location: "header",
            href: "/#contact",
          });
          onHashLinkClick(event, "/#contact");
        }}
        onNavClick={(item, event) => {
          trackClick("nav_anchor_click", {
            location: "header",
            label: item.label,
            href: item.href,
          });
          onHashLinkClick(event, item.href);
        }}
      />

      <Routes>
        <Route
          path="/"
          element={
            <main className="main" id="top">
        {/* SECTION 1: INTRO (HERO) */}
        <section className="section section--with-glow hero" aria-label="Hero">
          <div className="ambient-glow ambient-glow--hero-1" aria-hidden="true" />
          <div className="ambient-glow ambient-glow--hero-2" aria-hidden="true" />
          <div className="container">
            <h1 className="hero__headline">
              {homepageCopy.hero.headlineLine1}
              <span className="hero__headlineBreak">
                {homepageCopy.hero.headlineLine2}
              </span>
            </h1>
            <p className="hero__tagline">{homepageCopy.hero.tagline}</p>

            <div className="hero__ctaRow">
              <a
                className="btn btn--primary"
                href="/#contact"
                onClick={(event) => {
                  trackClick("demo_cta_click", {
                    location: "hero",
                    href: "/#contact",
                  });
                  onHashLinkClick(event, "/#contact");
                }}
              >
                {homepageCopy.hero.primaryCta}
              </a>
              <a
                className="btn btn--secondary"
                href="#solution"
                onClick={(event) => onHashLinkClick(event, "#solution")}
              >
                {homepageCopy.hero.secondaryCta}
              </a>
            </div>

            <div className="trustbar" aria-label="Trust and compliance">
              {homepageCopy.trustBar.map((item, index) => (
                <Fragment key={item}>
                  {index > 0 ? (
                    <span className="trustbar__sep" aria-hidden="true">
                      |
                    </span>
                  ) : null}
                  <span className="trustbar__item">{item}</span>
                </Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 2: YOUR PROBLEM */}
        <section className="section section--muted" id="problem">
          <div className="container">
            <p className="section__label">{homepageCopy.problem.label}</p>
            <h2 className="section__headline">{homepageCopy.problem.headline}</h2>
            <p className="section__intro">{homepageCopy.problem.intro}</p>

            <div className="cards cards--3">
              <article className="card">
                <h3 className="card__title">For Health Systems</h3>
                <ul className="card__list">
                  <li>
                    Valuable EHR, claims, and genomic data{" "}
                    <strong>unlock</strong> diverse new revenue streams
                  </li>
                  <li>
                    Direct-to-market models <strong>maximize</strong> your share
                    beyond the 5–10% offered by traditional aggregators
                  </li>
                  <li>
                    Turnkey framework <strong>accelerates</strong> your
                    high-value data partnerships
                  </li>
                </ul>
              </article>
              <article className="card">
                <h3 className="card__title">For Data Buyers</h3>
                <ul className="card__list">
                  <li>
                    A unified data ecosystem <strong>breaks traditional silos</strong>{" "}
                    to provide seamless researcher access
                  </li>
                  <li>
                    A streamlined partnership framework{" "}
                    <strong>fast-tracks the timeline</strong> for clinical discovery
                  </li>
                  <li>
                    An expansive provider network{" "}
                    <strong>closes diversity and geographic gaps</strong> in clinical
                    data
                  </li>
                </ul>
              </article>
              <article className="card">
                <h3 className="card__title">For Patients</h3>
                <ul className="card__list">
                  <li>
                    Correctly consented data <strong>empowers researchers</strong> to
                    fuel life-saving breakthroughs
                  </li>
                  <li>
                    A privacy-first model enables{" "}
                    <strong>secure collaboration</strong> and continuous scientific
                    progress
                  </li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        {/* SECTION 3: HOW WE FIX IT */}
        <section className="section section--with-glow" id="solution">
          <div className="ambient-glow ambient-glow--solution-1" aria-hidden="true" />
          <div className="ambient-glow ambient-glow--solution-2" aria-hidden="true" />
          <div className="container">
            <p className="section__label">{homepageCopy.solution.label}</p>
            <h2 className="section__headline">{homepageCopy.solution.headline}</h2>
            <p className="section__intro">{homepageCopy.solution.intro}</p>

            <div className="cards cards--4 cards--alt-neutral">
              {homepageCopy.solution.steps.map((step, index) => (
                <article className="card" key={step.title}>
                  <div className="card__stepHeader">
                    <span className="card__step" aria-hidden="true">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="card__title card__title--step">{step.title}</h3>
                      <p className="card__summary">{step.summary}</p>
                    </div>
                  </div>
                  <p className="card__body">{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4: HOW WE'RE DIFFERENT */}
        <section className="section section--muted">
          <div className="container">
            <p className="section__label">We&apos;re better at fixing it</p>
            <h2 className="section__headline">Built Different. On Purpose.</h2>
            <p className="section__intro">
              We&apos;re not another data aggregator. We&apos;re partnership
              infrastructure — customized to your institution, not forced on it.
            </p>

            <div className="cards cards--5">
              <article className="card">
                <h3 className="card__title">Accessed in Place</h3>
                <p className="card__body">
                  Buyers bring their own analytical environment to where the
                  data lives. Accessed in place — LooporaData never receives a
                  copy.
                </p>
              </article>
              <article className="card">
                <h3 className="card__title">Revenue Share</h3>
                <p className="card__body">
                  You set the price, you generated the data — you capture the
                  value.
                </p>
              </article>
              <article className="card">
                <h3 className="card__title">Go live in Weeks</h3>
                <p className="card__body">
                  Standardized agreement framework. Governance and IP stay with
                  you.
                </p>
              </article>
              <article className="card">
                <h3 className="card__title">AI-Ready Data Matching</h3>
                <p className="card__body">
                  Intelligent matching by disease area, population, outcomes,
                  and format — with automated deal proposals, not just listings.
                </p>
              </article>
              <article className="card">
                <h3 className="card__title">Standards-Based Permissions</h3>
                <p className="card__body">
                  GA4GH Data Use Ontology (DUO) permission modeling, so a buyer
                  can see what your existing consent permits.
                </p>
              </article>
            </div>

          </div>
        </section>

        {/* SECTION 5: SIDE-BY-SIDE COMPARISON */}
        <section className="section">
          <div className="container">
            <p className="section__label">Side-by-Side Comparison</p>
            <h2 className="section__headline">See the Difference, Clearly</h2>
            <p className="section__intro">
              Transparent economics, stronger governance, and faster time-to-value
              for sellers and buyers.
            </p>
            <div className="tableWrap" aria-label="Comparison table">
              <table className="compare">
                <thead>
                  <tr>
                    <th scope="col">Attribute</th>
                    <th scope="col">LooporaData</th>
                    <th scope="col">Aggregators</th>
                    <th scope="col">DIY</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Data custody</td>
                    <td>Accessed in place</td>
                    <td>Copied out</td>
                    <td>Usually copied</td>
                  </tr>
                  <tr>
                    <td>Time to go live</td>
                    <td>Weeks</td>
                    <td>Months</td>
                    <td>Months</td>
                  </tr>
                  <tr>
                    <td>Legal complexity</td>
                    <td>Pre-negotiated</td>
                    <td>Complex</td>
                    <td>Custom</td>
                  </tr>
                  <tr>
                    <td>Listing</td>
                    <td>Reviewed before listing</td>
                    <td>Varies</td>
                    <td>None</td>
                  </tr>
                  <tr>
                    <td>Solutions approach</td>
                    <td>Tailored for you</td>
                    <td>One-size-fits-all</td>
                    <td>Build it</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* SECTION 6: CONTACT US */}
        <section className="section section--with-glow contact" id="contact">
          <div className="ambient-glow ambient-glow--contact-1" aria-hidden="true" />
          <div className="ambient-glow ambient-glow--contact-2" aria-hidden="true" />
          <div className="container contact__inner">
            <p className="section__label">{homepageCopy.contact.label}</p>
            <h2 className="section__headline">{homepageCopy.contact.headline}</h2>
            <p className="section__intro">{homepageCopy.contact.intro}</p>

            <div className="contact__form" aria-label="Discover your data's value">
              <a
                className="btn btn--secondary"
                href={DEMO_MAILTO_HREF}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackClick("demo_cta_click", {
                    location: "contact",
                    href: DEMO_MAILTO_HREF,
                  })
                }
              >
                {homepageCopy.contact.ctaLabel}
              </a>
            </div>
            <p className="demo-disclaimer">
              <em>
                By connecting with us, you agree to our{" "}
                <a className="link" href="/privacy">
                  Privacy Policy
                </a>
                .
              </em>
            </p>
          </div>
        </section>
            </main>
          }
        />
        <Route
          path="/privacy"
          element={
            <main className="main" id="top">
              <section className="section section--legal" aria-label="Privacy notice">
                <div className="container">
                  <nav className="content-page__nav" aria-label="Page">
                    <Link className="link content-page__back" to="/">
                      Back to home
                    </Link>
                  </nav>
                  <article className="content-page">
                    <h1 className="content-page__h1">LooporaData Privacy Notice</h1>
                    <p>
                      <strong>Last updated:</strong> May 21, 2026
                    </p>

                    <h2 className="content-page__h2">Overview</h2>
                    <p>
                      LooporaData values your privacy. This policy explains how we
                      collect and use information when you visit our website or interact
                      with our marketing. Our analytics approach is designed to respect
                      your privacy. We do not use cookies, we do not track your activity
                      across other websites, and we do not store your full IP address.
                      All data is processed in an aggregate format or through anonymized
                      individual sessions.
                    </p>

                    <h2 className="content-page__h2">Information We Collect</h2>
                    <p>
                      We use privacy-first analytics that do not use cookies or collect
                      personal data. We track basic usage to improve our site experience.
                    </p>
                    <p>We collect information in two ways:</p>
                    <ul className="content-page__list">
                      <li>
                        <strong>Information You Provide:</strong> If you contact us via
                        email, sign up for a newsletter, or fill out a form, we collect
                        your name, email address, and the content of your message.
                      </li>
                      <li>
                        <strong>Automatic Usage Data:</strong> We automatically analyze
                        site interactions to improve performance. This data is
                        anonymized and never linked to personal identifiers like your name
                        or email.
                      </li>
                    </ul>
                    <p>
                      In accordance with strict data minimization standards, we only
                      collect personal data that is reasonably necessary and proportionate
                      to fulfill your specific requests. Furthermore, we do not collect or
                      use sensitive personal information for purposes other than those
                      strictly necessary to provide our services or as otherwise permitted
                      under applicable state laws.
                    </p>
                    <p>We collect limited personal data in the following categories:</p>
                    <div className="content-page__table-wrap">
                      <table className="content-page__table">
                        <thead>
                          <tr>
                            <th scope="col">Category</th>
                            <th scope="col">Examples</th>
                            <th scope="col">Source</th>
                            <th scope="col">Purpose</th>
                            <th scope="col">Retention</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Identifiers</td>
                            <td>Name, email address</td>
                            <td>Provided by you</td>
                            <td>Respond to inquiries, communication</td>
                            <td>Up to 12 months after last contact</td>
                          </tr>
                          <tr>
                            <td>Communications</td>
                            <td>Message content</td>
                            <td>Provided by you</td>
                            <td>Respond to requests</td>
                            <td>Up to 12 months</td>
                          </tr>
                          <tr>
                            <td>Internet Activity (Aggregated)</td>
                            <td>Page views, site interactions (non-identifiable)</td>
                            <td>Automatic</td>
                            <td>Website performance monitoring</td>
                            <td>Aggregated immediately</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <h2 className="content-page__h2">How We Use Your Data</h2>
                    <p>We use your information strictly to:</p>
                    <ul className="content-page__list">
                      <li>Respond to your inquiries.</li>
                      <li>Monitor and improve website performance.</li>
                      <li>Comply with legal obligations.</li>
                    </ul>
                    <p>
                      <strong>
                        We do not sell or share your personal information to third
                        parties.
                      </strong>
                    </p>

                    <h2 className="content-page__h2">
                      AI Governance &amp; Data Protection
                    </h2>
                    <p>
                      To protect our Intellectual Property (IP) and your data,
                      LooporaData enforces strict AI safety protocols:
                    </p>
                    <ul className="content-page__list">
                      <li>
                        <strong>No Third-Party Training:</strong> We strictly prohibit
                        company-controlled data (including PII, personal data, or ePHI)
                        from being used to train, develop, or improve third-party AI
                        models.
                      </li>
                      <li>
                        <strong>Permitted Internal Use:</strong> We utilize AI tools to
                        analyze data and generate insights for our own business
                        operations; this processing occurs within secure, private
                        environments where data is not fed back into the vendor&apos;s
                        general model.
                      </li>
                      <li>
                        <strong>Restrictive Settings:</strong> All third-party vendors
                        must be configured to their most restrictive privacy settings. We
                        opt-out of agentic and training features that would allow vendors
                        to access our databases or logs.
                      </li>
                    </ul>

                    <h3 className="content-page__h3">Data Use Safeguards</h3>
                    <p>
                      We apply strict controls to personal data collected through this
                      website:
                    </p>
                    <ul className="content-page__list">
                      <li>
                        <strong>No Third-Party Use:</strong> Personal data is not
                        disclosed to third parties for their own purposes, including model
                        development or similar activities.
                      </li>
                      <li>
                        <strong>Purpose Limitation:</strong> Data is used only for the
                        purposes described in this notice and is not used for unrelated
                        activities.
                      </li>
                    </ul>
                    <p>
                      <strong>Controlled Service Providers:</strong> Where service
                      providers are engaged, they process personal data solely on our
                      instructions and are contractually prohibited from retaining, using,
                      or disclosing the data for any purpose other than providing services
                      to us.
                    </p>

                    <h2 className="content-page__h2">Security &amp; Retention</h2>
                    <ul className="content-page__list">
                      <li>
                        <strong>Security:</strong> We use industry-standard encryption
                        and secure hosting providers to protect your data.
                      </li>
                      <li>
                        <strong>Retention:</strong> We only keep your personal data for as
                        long as is necessary to fulfill the purposes we collected it for,
                        or as required by law.
                      </li>
                      <li>
                        We maintain administrative, technical, and physical safeguards
                        appropriate to the sensitivity of the data.
                      </li>
                    </ul>

                    <h2 className="content-page__h2">Your Rights</h2>
                    <p>Regardless of where you live, we support your right to:</p>
                    <ul className="content-page__list">
                      <li>Access or Correct your personal data.</li>
                      <li>
                        Delete your data (the &quot;Right to be Forgotten&quot;).
                      </li>
                      <li>Object to processing or withdraw consent.</li>
                      <li>
                        Opt-Out of Data Sharing — as stated above, we do not sell or
                        share your personal information with third parties, so there
                        is no tracking-based sharing to opt out of.
                      </li>
                    </ul>
                    <p>
                      To exercise these rights, please contact us at{" "}
                      <a className="link" href="mailto:trust@looporadata.ai">
                        trust@looporadata.ai
                      </a>
                      . We may need to verify your identity before fulfilling requests;
                      you can appoint an agent to exercise your rights.
                    </p>

                    <h2 className="content-page__h2">Third-Party Links</h2>
                    <p>
                      Our site may link to other websites. We are not responsible for
                      their privacy practices, so please check their policies when you
                      leave our site.
                    </p>

                    <h2 className="content-page__h2">
                      Changes to This Privacy Notice
                    </h2>
                    <p>
                      We may update this notice periodically. Updates will be posted on
                      this page with a revised effective date.
                    </p>

                    <h2 className="content-page__h2">Contact Us</h2>
                    <p>For any questions regarding this policy, please contact:</p>
                    <address className="content-page__address">
                      <strong>LooporaData Privacy Team</strong>
                      <br />
                      Email:{" "}
                      <a className="link" href="mailto:trust@looporadata.ai">
                        trust@looporadata.ai
                      </a>
                      <br />
                      Address: LooporaData, Inc, 490 43rd Street, Oakland, CA 94609
                    </address>
                  </article>
                </div>
              </section>
            </main>
          }
        />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/investors" element={<InvestorsPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <SiteFooter
        onLinkedInClick={() =>
          trackClick("social_link_click", {
            location: "footer",
            platform: "linkedin",
          })
        }
        onCrunchbaseClick={() =>
          trackClick("social_link_click", {
            location: "footer",
            platform: "crunchbase",
          })
        }
      />
    </div>
  );
}

const KNOWN_ROUTES = new Set(["/", "/privacy", "/terms", "/blog", "/investors"]);

function getPageName(pathname) {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/privacy") return "privacy";
  if (path === "/terms") return "terms";
  if (path === "/investors") return "investors";
  if (path === "/blog") return "blog_index";
  if (path.startsWith("/blog/")) return "blog_post";
  if (!KNOWN_ROUTES.has(path)) return "not_found";
  return "home";
}

export default App;
